import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import type { useMsal } from '@azure/msal-react';
import type { Requester } from '@labeling/globals';

const logoutPromises = new WeakMap<ReturnType<typeof useMsal>['instance'], Promise<void>>();
let authRedirectPending = false;
let waitingForInteraction = false;

export const isAuthRedirectPending = () => authRedirectPending;

export const clearFinishedAuthInteraction = () => {
  if (!waitingForInteraction) return;
  waitingForInteraction = false;
  authRedirectPending = false;
};

const handleTokenError = async (
  msal: ReturnType<typeof useMsal>,
  error: unknown
): Promise<never> => {
  if (!(error instanceof InteractionRequiredAuthError)) throw error;

  authRedirectPending = true;
  if (msal.inProgress !== InteractionStatus.None && !logoutPromises.has(msal.instance)) {
    waitingForInteraction = true;
    throw error;
  }

  waitingForInteraction = false;
  try {
    let logoutPromise = logoutPromises.get(msal.instance);
    if (!logoutPromise) {
      logoutPromise = msal.instance.logoutRedirect({
        account: msal.instance.getActiveAccount(),
        postLogoutRedirectUri: window.location.origin,
      });
      logoutPromises.set(msal.instance, logoutPromise);
    }
    await logoutPromise;
  } catch (logoutError) {
    logoutPromises.delete(msal.instance);
    authRedirectPending = false;
    if (
      logoutError instanceof BrowserAuthError &&
      logoutError.errorCode === 'interaction_in_progress'
    ) {
      throw error;
    }
    throw logoutError;
  }
  throw error;
};

/**
 * Build a fetch function for sandbox mode that injects x-sandbox-requester-*
 * headers so the server reflects the currently assumed role.
 */
export const buildSandboxFetch =
  (assumedRole: Requester | null) =>
  (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [input, init] = args;
    const request = input instanceof Request ? input : new Request(input, init);
    const headers = new Headers(request.headers);
    if (assumedRole) {
      headers.set('x-sandbox-requester-type', assumedRole.type);
      headers.set('x-sandbox-requester-oid', assumedRole.oid);
      if (assumedRole.type === 'user') {
        headers.set('x-sandbox-requester-applications', assumedRole.applications.join(','));
      } else if (assumedRole.type === 'operator') {
        headers.set('x-sandbox-requester-applications', assumedRole.operatorApplications.join(','));
      }
    }
    return fetch(new Request(request, { ...init, headers }));
  };

/**
 * Build a fetch function for dev/prod modes that attaches an MSAL-issued
 * Bearer token for the given client's app registration. Shared by the API
 * and assistant clients since both sit behind the same Azure app registration.
 */
export const buildAuthenticatedFetch =
  (msal: ReturnType<typeof useMsal>, clientId: string) =>
  async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const { accessToken } = await msal.instance
      .acquireTokenSilent({ scopes: [`api://${clientId}/user_impersonation`] })
      .catch((error: unknown) => handleTokenError(msal, error));
    const [input, init] = args;
    const request = input instanceof Request ? input : new Request(input, init);
    const headers = new Headers(request.headers);
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', `Bearer ${accessToken}`);

    return fetch(new Request(request, { ...init, headers }));
  };
